import React, { useContext } from 'react'
import cn from 'classnames'
import { FormattedMessage } from 'react-intl'
import { ComponentLinkTargetTab, TypeItemEventOrArticle, TypeComponentLink } from '../../../lib/types'
import { ContentProps } from '../../renderer'
import { Typography } from '@grammarly/badger-ui'
import { ImageAsset } from '../../asset/image'
import { ExternalLink } from '../../ui-lib/link'
import { gnarSpec } from '../../../lib/tracking/gnar'
import { PageContext } from '../../../contexts/page-context'
import { PlacementContext } from '../../../contexts/placement-context'
import { Badge } from './badge'
import { CircularImages } from './circular-images'

import { EventIcon } from '../../ui-lib/icons/event'
import { MicrophoneIcon } from '../../ui-lib/icons/microphone'

import styles from './event-or-article.module.scss'

const { C4 } = Typography

interface EventOrArticleProps extends ContentProps<TypeItemEventOrArticle> {
  size?: string
  link: TypeComponentLink
}

export const EventOrArticle: React.FC<EventOrArticleProps> = ({ structure, size, link }) => {
  const {
    fields: { tags, dateAndTime, description, eyebrowTitle, header, image, badges, guestSpeakers, circularImages },
  } = structure

  const { relationToUrl, title, targetTab, targetUrl } = link.fields

  const hasDescription = description !== undefined
  const { pageId, slug } = useContext(PageContext)
  const { placement, placementInternalName } = useContext(PlacementContext)

  const isLarge = size === 'large'

  return (
    <ExternalLink
      href={targetUrl}
      target={targetTab === ComponentLinkTargetTab.Blank ? '_blank' : '_self'}
      rel={relationToUrl?.join(' ')}
      title={title}
      onClick={() => {
        void gnarSpec.linkClick(pageId, slug, targetUrl, header, placement, placementInternalName)
      }}
      className={cn(styles.eventOrArticle, { [styles.large]: isLarge })}
    >
      <div className={styles.imageContainer}>
        {image && <ImageAsset className={styles.image} structure={image} />}
        {circularImages?.length && (
          <CircularImages className={styles.circularImages} isLarge={isLarge} images={circularImages} />
        )}
      </div>
      {!!badges?.length && <Badge name={badges[0]} />}

      <div className={styles.contents}>
        {eyebrowTitle && <div className={styles.eyebrow}>{eyebrowTitle}</div>}
        {header && <div className={cn(styles.header, { [styles.noDescription]: !hasDescription })}>{header}</div>}
        {description && (
          <C4 as="div" className={styles.description}>
            {description}
          </C4>
        )}
        {(dateAndTime || guestSpeakers?.length) && (
          <div className={styles.infoBoxContainer}>
            {dateAndTime && (
              <div className={styles.infoBox}>
                <span className={styles.infoBoxIcon}>
                  <EventIcon color="neutral100" />
                </span>
                <div>
                  <FormattedMessage
                    id="eventOrArticle.dateAndTime.copy"
                    description="Event or Article - Date and Time - Copy"
                    defaultMessage="<strong>Event Date:</strong> {date}"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                      date: new Date(dateAndTime).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }),
                    }}
                  />
                </div>
              </div>
            )}
            {guestSpeakers?.length && (
              <div className={styles.infoBox}>
                <span className={styles.infoBoxIcon}>
                  <MicrophoneIcon color="neutral100" />
                </span>
                <div>
                  <FormattedMessage
                    id="eventOrArticle.guestSpeakers.copy"
                    description="Event or Article - Guest Speakers - Copy"
                    defaultMessage="<strong>Guest speakers:</strong> {speakers}"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                      speakers: guestSpeakers.join('; '),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {tags && (
          <div className={styles.tags}>
            {tags.map((tag, i) => {
              return (
                <div key={i} className={styles.tag}>
                  {tag}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </ExternalLink>
  )
}
